// ============================================================
// The wire shape of the wings: document ids, a tolerant reader for the
// scene record, and the cast diff a re-park needs.
// ============================================================
import { readTeamArrivals } from '../battlefield/deployment';
import { withTeamPoints } from './park';
import { PHASES } from '../phase/types';
import type { TurnTrackerState } from '../../types';
import { makeToken, type Token } from '../battlefield/tokens';
import type { DeploymentPoint } from '../battlefield/deployment';
import { emptyTurnTracker } from '../factories';
import { normalizeTurnTrackerState } from '../turnTracker';
import type { HiddenMark, SceneRecord } from './types';
import { readOrigin } from '../assets/provenance';
import type { Origin } from '../assets/types';

/** `sceneCast/{sceneId--combatantId}` - the same separator the map library's parts use. */
export function castDocId(sceneId: string, combatantId: string): string {
  return `${sceneId}--${combatantId}`;
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object';
const isPlaced = (v: unknown): v is { x: number; z: number } =>
  isRecord(v) && typeof v.x === 'number' && typeof v.z === 'number';

function marksFrom(raw: unknown): Record<string, Token> {
  const marks: Record<string, Token> = {};
  if (!isRecord(raw)) return marks;
  for (const [id, value] of Object.entries(raw)) {
    // THROUGH makeToken, so a mark a file left short is whole: the token
    // write names every field, and a field it cannot find is erased.
    if (isPlaced(value)) marks[id] = makeToken(id, { ...(value as Partial<Token>), id });
  }
  return marks;
}

function trackerFrom(raw: unknown): TurnTrackerState {
  const normalized = normalizeTurnTrackerState((raw as TurnTrackerState) ?? null);
  return normalized && Array.isArray(normalized.participants) ? normalized : emptyTurnTracker();
}

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const num = (v: unknown, fallback: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);

/**
 * A scene record from whatever the document holds. Tolerant: an older or
 * hand-written record is completed, and only a missing id or name is
 * fatal. Returns exactly the record's keys, so a round trip is equal.
 */
export function sceneFromDoc(raw: unknown): SceneRecord | null {
  if (!isRecord(raw) || typeof raw.id !== 'string' || typeof raw.name !== 'string') return null;
  return {
    id: raw.id,
    name: raw.name,
    order: num(raw.order, 0),
    notes: str(raw.notes),
    narration: str(raw.narration),
    mapName: typeof raw.mapName === 'string' ? raw.mapName : null,
    marker: isPlaced(raw.marker) ? (raw.marker as DeploymentPoint) : null,
    tracker: trackerFrom(raw.tracker),
    marks: marksFrom(raw.marks),
    castIds: Array.isArray(raw.castIds) ? raw.castIds.filter((id): id is string => typeof id === 'string') : [],
    lastOnStageAt: typeof raw.lastOnStageAt === 'number' ? raw.lastOnStageAt : null,
    createdAt: num(raw.createdAt, 0),
    phase: PHASES.find((phase) => phase === raw.phase) ?? null,
    hidden: hiddenFrom(raw.hidden),
    ...withTeamPoints(readTeamArrivals(raw.arrivals)),
    ...originOf(raw.origin),
  };
}

/** The library origin, when the record has a whole one; spread, so a record without one keeps its keys. */
function originOf(raw: unknown): { origin?: Origin } {
  const origin = readOrigin(raw);
  return origin ? { origin } : {};
}

/** The hidden marks, each one whole or dropped; an old scene with none reads as nobody hidden. */
function hiddenFrom(raw: unknown): Record<string, HiddenMark> {
  if (!isRecord(raw)) return {};
  const whole = (m: unknown): m is HiddenMark =>
    isRecord(m) && typeof m.alias === 'string' && typeof m.bornHidden === 'boolean';
  return Object.fromEntries(
    Object.entries(raw)
      .filter((entry): entry is [string, HiddenMark] => whole(entry[1]))
      .map(([id, m]) => [id, { alias: m.alias, initiative: m.initiative ?? null, bornHidden: m.bornHidden }]),
  );
}

/** The cast documents a re-park must delete: parked last time, not this time. */
export function castDiff(prevIds: readonly string[], nextIds: readonly string[]): { remove: string[] } {
  const keep = new Set(nextIds);
  return { remove: prevIds.filter((id) => !keep.has(id)) };
}
